<template>
	<div class="container" id="profile_section">
		<div v-if="user_level < 90">
			<h5 class="text-danger text-center mt-5">Access Denied!</h5>
		</div>

		<div v-else>
			<div class="page-header gold mt-4 mb-2">
				<back-btn to="/"></back-btn>
				<h3 class="mb-0">
					<i class="fas fa-graduation-cap pr-1"></i>
					<span v-text="this.$route.meta.title"></span> (<small v-text="totalRows"></small>)
				</h3>
			</div>

			<div class="row mb-2">
				<div class="col-12 text-right">
					<button type="button" class="btn btn-sm btn-outline-secondary" @click="courseAdd">
						<i class="fas fa-graduation-cap"></i> Add New Course
					</button>
				</div>
			</div>

			<b-modal
				id="modal-course"
				:title="modal.title"
				ref="modal-course"
				no-close-on-backdrop
				size="lg"
			>
				<b-form @submit="submitCourseForm($event)">
					<b-form-group>
						<small class="text-muted">Status</small>
						<b-form-select
							v-model="modal.course.status"
							:options="statuses"
							@change="validate(modal.course)"
						></b-form-select>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Name</small>
						<b-form-input
							v-model="modal.course.title"
							type="text"
							required
							placeholder="Enter course name"
							@change="validate(modal.course)"
						></b-form-input>
						<small class="text-danger" v-if="modal.course.title === ''">
							Enter a course name
						</small>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Slug</small>
						<b-form-input
							v-model="modal.course.slug"
							type="text"
							required
							placeholder="Enter course slug"
							@change="validate(modal.course)"
						></b-form-input>
						<small class="text-danger" v-if="modal.course.slug === ''">
							Enter a course slug
						</small>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Category</small>
						<b-form-select
							v-model="modal.course.category"
							:options="categories"
						></b-form-select>
						<small class="text-danger" v-if="modal.course.category === null">
							Select Category
						</small>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Icon</small>
						<b-form-input
							v-model="modal.course.icon"
							type="text"
							required
							placeholder="Enter course icon"
							@change="validate(modal.course)"
						></b-form-input>
						<small class="text-danger" v-if="modal.course.icon === ''">
							Enter a course icon
						</small>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Type</small>
						<b-form-input
							v-model="modal.course.type"
							type="text"
							required
							placeholder="Enter course type"
							@change="validate(modal.course)"
						></b-form-input>
						<small class="text-danger" v-if="modal.course.type === ''">
							Enter a course type
						</small>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Description</small>
            <vue-editor v-model="modal.course.description"></vue-editor>
            <small class="text-danger" v-if="modal.course.description === ''">
							Enter a course description
						</small>
					</b-form-group>

					<b-form-group>
						<small class="text-muted">Sort Order</small>
						<b-form-input
							v-model="modal.course.sort"
							type="text"
							required
							placeholder="Sort order"
							@change="validate(modal.course)"
						></b-form-input>
						<small class="text-danger" v-if="modal.course.sort === ''">
							Enter a sort order
						</small>
					</b-form-group>
        </b-form>

				<p><small class="text-danger" v-text="modal.errors.process"></small></p>

				<template #modal-footer="{ cancel }">
					<div class="w-100 row">
						<div class="col text-left">
							<b-button
								type="button"
								size="md"
								variant="success"
								:disabled="modal.submitDisabled"
								@click="submitCourseForm()"
								>Save
							</b-button>
						</div>
						<div class="col text-right">
							<b-button
								size="sm"
								@click="confirmBox()"
								name="delete"
								variant="outline-danger"
								>Delete
							</b-button>
							<b-button size="sm" variant="outline-secondary" @click="cancel('cancel')">
								cancel
							</b-button>
						</div>
					</div>
				</template>
			</b-modal>

			<div class="row" id="course_wrap">
				<div class="col-md-6 my-1">
					<b-form-group class="mb-0">
						<b-input-group size="sm">
							<b-form-input
								id="filter-input"
								v-model="filter"
								type="search"
								placeholder="Search courses"
							></b-form-input>

							<b-input-group-append>
								<b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
							</b-input-group-append>
						</b-input-group>
					</b-form-group>
				</div>

				<div class="col-md-3 col-4 my-1">
					<b-form-group class="mb-0">
						<b-form-select
							id="per-page-select"
							v-model="perPage"
							:options="pageOptions"
							size="sm"
						></b-form-select>
					</b-form-group>
				</div>

				<div class="col-md-3 col-8 my-1">
					<b-pagination
						v-model="currentPage"
						:total-rows="totalRows"
						:per-page="perPage"
						align="right"
						size="sm"
						class="my-0"
					></b-pagination>
				</div>

				<div class="col-12">
					<b-table
						bordered
						responsive
						:items="courses"
						:busy="isBusy"
						:fields="fields"
						:filter="filter"
						:filter-included-fields="filterOn"
						@filtered="onFiltered"
						:current-page="currentPage"
						:per-page="perPage"
						:sort-by.sync="sortBy"
						:sort-desc.sync="sortDesc"
						sort-icon-left
						class="small"
						show-empty
					>
						<template v-slot:table-busy>
							<div class="text-center my-2 p-2">
								<b-spinner class="align-middle"></b-spinner>
								<br />
								<strong>Loading...</strong>
							</div>
						</template>

						<template v-slot:cell(description)="data">
							<text-clamp :html="data.item.description" />
						</template>

						<template v-slot:cell(status)="data">
							<span v-text="arrayElmGet(statuses, data.item.status)"></span>
						</template>

						<template v-slot:cell(category)="data">
							<span v-text="arrayElmGet(categories, data.item.category)"></span>
						</template>

						<template v-slot:cell(actions)="data">
							<b-dropdown
								size="sm"
								split
								split-variant="outline-primary"
								class="gold"
								text="Edit"
								@click="courseEdit($event, data.item)"
							>
								<b-dropdown-item>
									<div>
										<a href="#" @click="courseEdit($event, data.item)"> Course </a>
									</div>
								</b-dropdown-item>
								<b-dropdown-item>
									<div>
										<a @click.prevent="sectionsEdit(data.item)"> Sections</a>
									</div>
								</b-dropdown-item>
								<b-dropdown-item>
                  <a style="color: red!important;" @click.prevent="courseDuplicate(data.item.id)">Duplicate</a>
								</b-dropdown-item>
							</b-dropdown>
						</template>
					</b-table>
				</div>

				<div class="col-12 my-1">
					<b-pagination
						v-model="currentPage"
						:total-rows="totalRows"
						:per-page="perPage"
						align="right"
						size="sm"
						class="my-0"
					></b-pagination>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from '@/axios';
import BackBtn from '@/components/BackBtn.vue';
import { VueEditor } from 'vue2-editor';
import TextClamp from '@/components/programs/TextClamp.vue';

export default {
	name: 'CoursesAdmin',
	components: { BackBtn, VueEditor, TextClamp },
	props: {},
	data() {
		return {
			modal: {
				title: '',
				submitDisabled: true,
				course: {},
				errors: {
					process: '',
				},
			},

			modal_course_default: {
				id: 0,
				title: '',
				status: 1,
				// start_date: null,
			},

			statuses: [
				{ value: 0, text: 'Closed (deleted)' },
				{ value: 1, text: 'Active' },
				{ value: 2, text: 'Pending' },
			],

			errors: {},
			categories: [
        { value: 1, text: 'On-Demand' },
        { value: 2, text: 'Cohort-Based' },
        { value: 3, text: 'Meditation' },
				],
			courses: null,

			fields: [
				{ key: 'actions', sortable: false, label: 'Actions', class: '' },
				{ key: 'title', sortable: true, label: 'Title', class: '' },
				{ key: 'description', sortable: true, label: 'Description', class: '' },
				{ key: 'category', sortable: true, label: 'Category', class: '' },
				{ key: 'status', sortable: true, label: 'Status', class: '' },
				// { key: 'start_date', sortable: true, label: 'Start Date', class: '' },
				{ key: 'id', sortable: true, label: 'ID', class: '' },
				{ key: 'sort', sortable: true, label: 'Order', class: '' },
			],
			isBusy: true,
			sortBy: 'sort',
			sortDesc: false,
			totalRows: 0,
			currentPage: 1,
			perPage: 50,
			pageOptions: [
				{ value: 50, text: 'Num to Show' },
				25,
				50,
				100,
				{ value: 500, text: 'All' },
			],
			filter: null,
			filterOn: [],
		};
	},
	methods: {
		arrayElmGet(obj, value) {
			const entries = Object.entries(obj);
			for (const v of entries) {
				if (parseInt(v[1].value) === parseInt(value)) {
					return v[1].text;
				}
			}
			return null;
		},
		confirmBox() {
			this.$bvModal
				.msgBoxConfirm('Are you sure you want to delete this Course?')
				.then((value) => {
					if (value == true) {
						this.modal.course.status = 0;
						this.submitCourseForm();
					} else {
						this.$bvModal.hide('modal-course');
					}
				})
				.catch((err) => {
					console.log(err);
				});
		},
		sectionsEdit(item) {
			this.$router.push('/course_sections/' + item.id);
		},

		onFiltered(filteredItems) {
			this.totalRows = filteredItems.length;
			this.currentPage = 1;
		},
		submitCourseForm(e) {
			let _this = this;
			if (e) {
				e.preventDefault();
			}

			this.validate(_this.modal.course);

			_this.modal.errors.process = 'Processing...';

			let params = new URLSearchParams();

			if (_this.modal.course) {
				const obj = Object.entries(_this.modal.course);
				for (const [k, item] of obj) {
					if (k !== 'sections') {
						params.append('course[' + k + ']', item);
					}
				}
			}

			axios({
				method: 'post',
				url: 'courseAddEdit',
				data: params,
        headers: { 'Content-Type': 'multipart/form-data' },
			})
				.then(function (response) {
					_this.modal.errors.process = response.data.message;

					if (response.data.status == 1) {
						if (response.data.course !== null) {
							if (response.data.action_type == 'add') {
								_this.courses.unshift(response.data.course);
								_this.modal.course = _this.modal_course_default;
							} else {
								if (_this.modal.course.status === 0) {
									_this.courses = _this.courses.filter(
										(item) => item.id !== _this.modal.course.id
									);
								} else {
									_this.courses.find((obj) => {
										if (obj.id === _this.modal.course.id) {
                      Object.assign(obj, response.data.course);
										}
									});
								}
							}
						}

						_this.totalRows = _this.courses.length;

						_this.$refs['modal-course'].hide();
					} else {
						_this.modal.errors.process = response.data.message;
					}
				})
				.catch(function (e) {
					console.log('Error: submitCourseForm:' + e);
				});
		},
		courseAdd() {
			this.modal.course = this.modal_course_default;
			if (!this.modal.course.title) {
				this.modal.title = 'Add Course';
				this.modal.errors.process = '';
			}
			this.$bvModal.show('modal-course');
			this.validate(this.modal.course);
		},
		courseDuplicate(courseId) {
			axios.post(`/course/${courseId}/duplicate`)
				.then( (response) => {
					if (response.data.status == 1) {
            response.data.course.id = response.data.course.pkid;
						this.courses.unshift(response.data.course);
						this.totalRows = this.courses.length;
						this.$refs['modal-course'].hide();
					} else {
						this.modal.errors.process = response.data.message || 'Error duplicating course';
					}
				})
				.catch(function (e) {
					console.log('Error: courseDuplicate:' + e);
				});
		},
		courseEdit(e, item) {
			this.modal.title = 'Edit Course';
			this.modal.errors.process = '';
			this.modal.course = { ...item };
			this.$bvModal.show('modal-course');
			this.validate(this.modal.course);
		},
		coursesGet() {
			let _this = this;
			let params = new URLSearchParams();
			params.append('token', this.$store.state.session_id);
			axios({
				url: _this.$store.state.api_url + 'coursesGet',
				params: params,
			})
				.then(function (response) {
					_this.isBusy = false;
					if (response.data.status == 1) {
						_this.courses = Object.values(response.data.courses);
					} else {
						_this.courses = null;
					}
					_this.totalRows = _this.courses.length;
				})
				.catch(function () {
					console.log('Error: coursesGet.');
				});
		},

		validate(item) {
			var valid = true;
			if (item.status === '' || item.status === 'null') {
				valid = false;
			}
			if (item.title === '') {
				valid = false;
			}

			if (valid == true) {
				this.modal.submitDisabled = false;
				return false;
			} else {
				this.modal.submitDisabled = true;
				return true;
			}
		},
	},
	computed: {
		sortOptions() {
			return this.fields
				.filter((f) => f.sortable)
				.map((f) => {
					return { text: f.label, value: f.key };
				});
		},
		active_profile() {
			return this.$store.getters.profileGet;
		},
		user_level() {
			return this.active_profile.level;
		},
	},
	created() {},
	mounted() {
		this.coursesGet();
	},
	watch: {},
};
</script>

<style scoped>
.page-header {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 14px;
	position: relative;
	padding: 0 52px;
}
.page-header h3 {
	min-width: 0;
	text-align: center;
	word-break: break-word;
	font-size: clamp(1rem, 4vw, 1.75rem);
}

</style>
